import { useEffect, useRef, useState, KeyboardEvent } from 'react'
import { useAiChat } from '../hooks/useAiChat'
import { renderMarkdown } from '../lib/markdown'
import { LimitExceededBanner } from '../components/ui/LimitExceededBanner'

// ── Suggerimenti iniziali ──────────────────────────────────────────────────────
const SUGGESTIONS = [
  "Qual è il margine medio dei progetti questo trimestre?",
  "Analizza il trend dei ricavi degli ultimi 3 mesi.",
  "Quali clienti hanno fatture scadute da oltre 30 giorni?",
  "Riassumi lo stato dei ricavi ricorrenti e le scadenze imminenti.",
]

// ─────────────────────────────────────────────────────────────────────────────

export function AiChat() {
  const { messages, isStreaming, error, limitError, sendMessage, clearHistory } = useAiChat()
  const [draft, setDraft] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-scroll all'ultimo messaggio
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSend = () => {
    const text = draft.trim()
    if (!text || isStreaming) return
    setDraft('')
    sendMessage(text)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleSuggestion = (text: string) => {
    setDraft(text)
    textareaRef.current?.focus()
  }

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-white/[0.07]">
        <div>
          <h1 className="text-xl font-semibold text-[--km-text-primary]">AI Chat</h1>
          <p className="text-sm text-[--km-text-muted]">Analisi KPI in linguaggio naturale</p>
        </div>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={clearHistory}
            className="text-sm text-[--km-text-muted] hover:text-[--km-text-secondary] transition-colors"
            aria-label="Nuova conversazione"
          >
            Nuova conversazione
          </button>
        )}
      </div>

      {/* ── Messaggi ──────────────────────────────────────────────────────── */}
      <div
        className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4"
        aria-label="Cronologia messaggi"
        aria-live="polite"
      >
        {messages.length === 0 ? (
          /* Welcome screen */
          <div className="flex flex-col items-center justify-center h-full text-center space-y-6">
            <div className="w-14 h-14 rounded-full bg-indigo-500/10 flex items-center justify-center">
              <svg className="w-7 h-7 text-indigo-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-medium text-[--km-text-primary]">Come posso aiutarti?</h2>
              <p className="text-sm text-[--km-text-muted] mt-1 max-w-sm">
                Chiedi un'analisi dei tuoi KPI, confronta periodi, o esplora anomalie nei dati.
              </p>
            </div>
            {/* Suggerimenti */}
            <div className="grid grid-cols-1 gap-2 w-full max-w-lg">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => handleSuggestion(s)}
                  className="text-left px-4 py-3 rounded-xl border border-white/[0.07] text-sm text-[--km-text-secondary] hover:border-indigo-500/30 hover:bg-indigo-500/[0.06] transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* Elenco messaggi */
          <>
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${msg.role}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center mr-2 flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                    </svg>
                  </div>
                )}

                <div
                  className={[
                    'max-w-[80%] rounded-2xl px-4 py-3 text-sm',
                    msg.role === 'user'
                      ? 'bg-indigo-600 text-white rounded-tr-sm'
                      : 'bg-[--km-surface-2] border border-white/[0.07] text-[--km-text-primary] rounded-tl-sm',
                  ].join(' ')}
                >
                  {msg.role === 'user' ? (
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  ) : (
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                      className="prose prose-sm prose-invert max-w-none"
                    />
                  )}
                  {msg.streaming && (
                    <span className="inline-block w-1.5 h-4 ml-0.5 bg-indigo-400 animate-pulse align-middle" aria-hidden="true" />
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Limite piano raggiunto */}
        {limitError && (
          <div className="px-4">
            <LimitExceededBanner error={limitError} />
          </div>
        )}

        {/* Errore globale */}
        {error && (
          <div className="flex justify-center" role="alert">
            <p className="text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-lg px-4 py-2">
              {error}
            </p>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Input ─────────────────────────────────────────────────────────── */}
      <div className="px-4 sm:px-6 py-4 border-t border-white/[0.07] bg-[--km-bg]/80 backdrop-blur-sm">
        <div className="flex items-end gap-3">
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Scrivi un messaggio…"
            rows={1}
            disabled={isStreaming}
            aria-label="Testo del messaggio"
            className="flex-1 resize-none rounded-xl border border-white/[0.1] bg-[--km-surface-2] px-4 py-3 text-sm text-[--km-text-primary] placeholder-[--km-text-muted] focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/40 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!draft.trim() || isStreaming}
            aria-label="Invia messaggio"
            className="flex-shrink-0 rounded-xl bg-indigo-600 p-3 text-white hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {isStreaming ? (
              /* Spinner mentre stream attivo */
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            )}
          </button>
        </div>
        <p className="text-xs text-[--km-text-muted] mt-2 text-center">
          Invio per inviare · Shift+Invio per andare a capo · Le risposte si basano sui tuoi KPI.
        </p>
      </div>
    </div>
  )
}
